import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import logo from '../assets/chip_logo.png';
import '../styles/Login.css'; // Reusing Login styles for consistency

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Reset Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const Toast = {
    show: ({ type, text1 }) => alert(`[${type.toUpperCase()}] ${text1}`)
  };

  const validateEmail = (em) => /\S+@\S+\.\S+/.test(em);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setErrors({ email: "Email is required" });
      return;
    }
    if (!validateEmail(email)) {
      setErrors({ email: "Invalid email format" });
      return;
    }

    try {
      setLoader(true);
      const { data } = await api.post("/admin/auth/forgot-password", { email: email.trim().toLowerCase() });
      Toast.show({ type: "success", text1: data.message });
      setStep(2);
      setErrors({});
    } catch (error) {
      const message = error?.response?.data?.error ?? error?.message ?? "Failed to send OTP";
      Toast.show({ type: "error", text1: message });
    } finally {
      setLoader(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setErrors({ otp: "OTP is required" });
      return;
    }

    try {
      setLoader(true);
      const { data } = await api.post("/admin/auth/verify-otp", { 
        email: email.trim().toLowerCase(),
        otp: otp.trim()
      });
      Toast.show({ type: "success", text1: data.message });
      setStep(3);
      setErrors({});
    } catch (error) {
      const message = error?.response?.data?.error ?? error?.message ?? "Incorrect OTP";
      Toast.show({ type: "error", text1: message });
    } finally {
      setLoader(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) {
      setErrors({ newPassword: "Password is required" });
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    try {
      setLoader(true);
      const { data } = await api.post("/admin/auth/reset-password", { 
        email: email.trim().toLowerCase(),
        password: newPassword.trim()
      });
      Toast.show({ type: "success", text1: data.message });
      navigate('/login');
    } catch (error) {
      const message = error?.response?.data?.error ?? error?.message ?? "Failed to reset password";
      Toast.show({ type: "error", text1: message });
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="app-container">
      <header className="login-header">
        <div className="logo-container">
          <img src={logo} alt="Chip Logo" className="logo" onClick={() => navigate('/login')} style={{ cursor: 'pointer' }} />
        </div>
      </header>

      <main className="login-main-content">
        <div className="login-card">
          <h1>
            {step === 1 && "Forgot Password"}
            {step === 2 && "Verify OTP"}
            {step === 3 && "Reset Password"}
          </h1>
          <p className="subtitle">
            {step === 1 && "Enter your admin email to receive a 4-digit OTP."}
            {step === 2 && `Enter the 4-digit OTP sent to your registered phone.`}
            {step === 3 && "Create a new strong password for your admin account."}
          </p>

          {step === 1 && (
            <form className="form-group" onSubmit={handleSendOTP} noValidate>
              <div>
                <div className="input-container">
                  <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <input 
                    type="email" 
                    placeholder="Admin Email" 
                    className={`input-field ${errors.email ? 'input-error' : ''}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {errors.email && <p className="error-text">{errors.email}</p>}
              </div>
              <button type="submit" className="submit-btn" disabled={loader}>
                {loader ? "Sending..." : "Send OTP"}
              </button>
              <p className="back-to-login" onClick={() => navigate('/login')} style={{ textAlign: 'center', marginTop: '20px', cursor: 'pointer', color: 'var(--primary-color)', fontSize: '14px', fontWeight: '600' }}>
                Back to Login
              </p>
            </form>
          )}

          {step === 2 && (
            <form className="form-group" onSubmit={handleVerifyOTP} noValidate>
              <div>
                <div className="input-container">
                  <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                  <input 
                    type="text" 
                    placeholder="4-Digit OTP" 
                    maxLength="4"
                    className={`input-field ${errors.otp ? 'input-error' : ''}`}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                {errors.otp && <p className="error-text">{errors.otp}</p>}
              </div>
              <button type="submit" className="submit-btn" disabled={loader}>
                {loader ? "Verifying..." : "Verify OTP"}
              </button>
              <p className="back-to-login" onClick={() => setStep(1)} style={{ textAlign: 'center', marginTop: '20px', cursor: 'pointer', color: 'var(--primary-color)', fontSize: '14px', fontWeight: '600' }}>
                Change Email
              </p>
            </form>
          )}

          {step === 3 && (
            <form className="form-group" onSubmit={handleResetPassword} noValidate>
              <div>
                <div className="input-container">
                  <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    placeholder="New Password" 
                    className={`input-field ${errors.newPassword ? 'input-error' : ''}`}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <svg className="input-icon-right" onClick={() => setShowPassword(!showPassword)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showPassword ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                    )}
                  </svg>
                </div>
                {errors.newPassword && <p className="error-text">{errors.newPassword}</p>}
              </div>

              <div>
                <div className="input-container">
                  <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    placeholder="Confirm New Password" 
                    className={`input-field ${errors.confirmPassword ? 'input-error' : ''}`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                {errors.confirmPassword && <p className="error-text">{errors.confirmPassword}</p>}
              </div>

              <button type="submit" className="submit-btn" disabled={loader}>
                {loader ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
